import { useState } from "react";
import axios from "axios";
import { AnimatePresence, motion } from "framer-motion";
import { BadgeCheck, Zap } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Reveal } from "./Reveal";
import { COLORS, DRIVETRAINS, MOONROOF_OPTIONS, TRIMS } from "@/constants/options";
import { startCheckout } from "@/lib/pay";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Field = ({ label, children }) => (
    <div className="space-y-2">
        <Label className="text-zinc-300">{label}</Label>
        {children}
    </div>
);

const SelectField = ({ value, onChange, placeholder, options, testId }) => (
    <Select value={value} onValueChange={onChange}>
        <SelectTrigger data-testid={testId} className="h-12 rounded-none border-white/15 bg-black/40">
            <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
            {options.map((o) => (
                <SelectItem
                    key={o}
                    value={o}
                    data-testid={`${testId}-option-${o.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                >
                    {o}
                </SelectItem>
            ))}
        </SelectContent>
    </Select>
);

const SpotPayButton = ({ spotId }) => {
    const [paying, setPaying] = useState(false);
    const pay = async () => {
        setPaying(true);
        try {
            await startCheckout("spot_deal_fee", spotId);
        } catch {
            toast.error("Could not start card checkout — try again.");
            setPaying(false);
        }
    };
    return (
        <button
            data-testid="spot-pay-card-button"
            onClick={pay}
            disabled={paying}
            className="mt-8 flex h-14 w-full items-center justify-center gap-3 bg-[#2563EB] text-sm font-bold uppercase tracking-[0.25em] text-white transition-colors duration-300 hover:bg-white hover:text-black"
        >
            {paying ? "Redirecting to secure checkout..." : "Pre-pay $699 by card (optional)"}
        </button>
    );
};

export const SpotDeals = () => {
    const [form, setForm] = useState({
        name: "",
        email: "",
        phone: "",
        address: "",
        trim: "",
        color: "",
        drivetrain: "",
        moonroof: "",
    });
    const [submitting, setSubmitting] = useState(false);
    const [spot, setSpot] = useState(null);

    const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));
    const setVal = (key) => (v) => setForm((f) => ({ ...f, [key]: v }));

    const submit = async (e) => {
        e.preventDefault();
        if (!form.trim || !form.color || !form.drivetrain || !form.moonroof) {
            toast.error("Please select your trim, color, drivetrain, and moonroof preference");
            return;
        }
        setSubmitting(true);
        try {
            const { data } = await axios.post(`${API}/spot-deals`, form);
            if (window.rdt) window.rdt("track", "Lead");
            setSpot(data);
        } catch (err) {
            const detail = err.response?.data?.detail;
            const msg = Array.isArray(detail)
                ? detail.map((e) => `${e.loc?.slice(-1)[0] || "field"}: ${e.msg}`).join("; ")
                : typeof detail === "string"
                  ? detail
                  : err.response
                    ? `Server error (${err.response.status}) — please try again`
                    : "Network error — check your connection and try again";
            toast.error(msg);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <section id="spot-deals" data-testid="spot-deals-section" className="bg-[#080808] py-28 sm:py-36">
            <div className="mx-auto max-w-7xl px-6">
                <Reveal>
                    <p
                        data-testid="spot-vs-membership-text"
                        className="mx-auto mb-16 max-w-3xl text-center font-display text-lg font-bold uppercase leading-snug tracking-tight text-white sm:text-xl"
                    >
                        Both our Spot Deals and Membership options allocate vehicles below MSRP —
                        including our fees.{" "}
                        <span className="text-[#2563EB]">
                            Choose Spot Deals if you need a car within 0–7 days.
                        </span>{" "}
                        Choose Membership if you can wait 0–90 days.
                    </p>
                </Reveal>
            </div>
            <div className="mx-auto grid max-w-7xl grid-cols-1 gap-16 px-6 lg:grid-cols-2">
                <Reveal>
                    <p className="mb-4 flex items-center gap-3 text-xs font-bold uppercase tracking-[0.35em] text-[#2563EB]">
                        <Zap className="h-4 w-4" aria-hidden="true" />
                        Spot deals
                    </p>
                    <h2 className="font-display text-3xl font-extrabold uppercase leading-[1.02] tracking-tighter sm:text-5xl">
                        Your RAV4. On the lot. Now.
                    </h2>
                    <p className="mt-6 max-w-md text-base leading-relaxed text-zinc-400 md:text-lg">
                        We offer spot deals where your new RAV4 — in the trim you love, with
                        substantially most if not all of your favorite options — is on the dealer
                        lot or will be on the dealer lot within 1 week. Sign up below for spot
                        deals.
                    </p>
                    <div className="mt-10 border-l-2 border-[#2563EB] pl-6">
                        <p className="font-display text-5xl font-extrabold tracking-tighter">$699</p>
                        <p className="mt-2 text-xs uppercase tracking-[0.2em] text-zinc-500">
                            Spot Deal fee — due only once your vehicle is allocated
                        </p>
                    </div>
                </Reveal>

                <Reveal delay={0.15}>
                    <AnimatePresence mode="wait">
                        {spot ? (
                            <motion.div
                                key="success"
                                data-testid="spot-success-panel"
                                initial={{ opacity: 0, y: 32 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                                className="border border-[#2563EB]/40 bg-black/60 p-8 backdrop-blur-xl sm:p-10"
                            >
                                <BadgeCheck className="h-10 w-10 text-[#2563EB]" aria-hidden="true" />
                                <h3 className="mt-6 font-display text-2xl font-bold uppercase tracking-tight sm:text-3xl">
                                    You're on the list, {spot.name.split(" ")[0]}.
                                </h3>
                                <p className="mt-3 text-sm leading-relaxed text-zinc-400 sm:text-base">
                                    We'll notify you by text, email, or phone call the moment your{" "}
                                    <span className="text-white">{spot.trim}</span> in{" "}
                                    <span className="text-white">{spot.color}</span> (
                                    <span className="text-white">{spot.drivetrain}</span>,{" "}
                                    <span className="text-white">{spot.moonroof}</span>) is
                                    allocated — with all specs and the exact price. Then remit the
                                    $699 Spot Deal fee and pick up within 1 week — very often the
                                    same day.
                                </p>
                                <SpotPayButton spotId={spot.id} />
                            </motion.div>
                        ) : (
                            <motion.form
                                key="form"
                                data-testid="spot-deal-form"
                                onSubmit={submit}
                                exit={{ opacity: 0, y: -24 }}
                                transition={{ duration: 0.4 }}
                                className="space-y-6 border border-white/10 bg-[#0a0a0a] p-8 sm:p-10"
                            >
                                <Field label="Full name">
                                    <Input
                                        data-testid="spot-form-name-input"
                                        required
                                        minLength={2}
                                        placeholder="Alex Carter"
                                        value={form.name}
                                        onChange={set("name")}
                                        className="h-12 rounded-none border-white/15 bg-black/40"
                                    />
                                </Field>
                                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                                    <Field label="Email">
                                        <Input
                                            data-testid="spot-form-email-input"
                                            type="email"
                                            required
                                            placeholder="alex@email.com"
                                            value={form.email}
                                            onChange={set("email")}
                                            className="h-12 rounded-none border-white/15 bg-black/40"
                                        />
                                    </Field>
                                    <Field label="Telephone">
                                        <Input
                                            data-testid="spot-form-phone-input"
                                            type="tel"
                                            required
                                            minLength={7}
                                            placeholder="(555) 010-2026"
                                            value={form.phone}
                                            onChange={set("phone")}
                                            className="h-12 rounded-none border-white/15 bg-black/40"
                                        />
                                    </Field>
                                </div>
                                <Field label="Address">
                                    <Input
                                        data-testid="spot-form-address-input"
                                        required
                                        minLength={5}
                                        placeholder="Street, City, State, ZIP"
                                        value={form.address}
                                        onChange={set("address")}
                                        className="h-12 rounded-none border-white/15 bg-black/40"
                                    />
                                </Field>
                                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                                    <Field label="Trim package">
                                        <SelectField
                                            value={form.trim}
                                            onChange={setVal("trim")}
                                            placeholder="Select trim"
                                            options={TRIMS}
                                            testId="spot-form-trim-select"
                                        />
                                    </Field>
                                    <Field label="Color">
                                        <SelectField
                                            value={form.color}
                                            onChange={setVal("color")}
                                            placeholder="Select color"
                                            options={COLORS}
                                            testId="spot-form-color-select"
                                        />
                                    </Field>
                                    <Field label="Drivetrain">
                                        <SelectField
                                            value={form.drivetrain}
                                            onChange={setVal("drivetrain")}
                                            placeholder="AWD or FWD"
                                            options={DRIVETRAINS}
                                            testId="spot-form-drivetrain-select"
                                        />
                                    </Field>
                                    <Field label="Moonroof">
                                        <SelectField
                                            value={form.moonroof}
                                            onChange={setVal("moonroof")}
                                            placeholder="Moonroof option"
                                            options={MOONROOF_OPTIONS}
                                            testId="spot-form-moonroof-select"
                                        />
                                    </Field>
                                </div>
                                <Button
                                    data-testid="spot-form-submit-button"
                                    type="submit"
                                    disabled={submitting}
                                    className="h-14 w-full rounded-none bg-[#2563EB] text-sm font-bold uppercase tracking-[0.25em] text-white transition-colors duration-300 hover:bg-white hover:text-black"
                                >
                                    {submitting ? "Signing you up..." : "Sign up for spot deals"}
                                </Button>
                                <p className="text-center text-xs leading-relaxed text-zinc-500">
                                    Nothing due now. The $699 Spot Deal fee applies only after your
                                    vehicle is allocated.
                                </p>
                            </motion.form>
                        )}
                    </AnimatePresence>
                </Reveal>
            </div>
        </section>
    );
};
